import { Injectable } from '@angular/core';
import { ClientNotificationHandlerService, FileObject } from '@thehubz/client/core';
import { Asset, AssetsApiService, CreateAssetDto, CreateAssetFileDto, UpdateAssetAspectRatioDto, UpdateAssetManyDto } from '@thehubz/client/data-access-api';
import { FileUploadService } from '@thehubz/client/file-upload';
import { AnyType, AssetTypeEnum, assetTypeByMimetype } from '@thehubz/shared/model';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { AssetBannerHtmlService } from './asset-banner-html.service';

export interface AssetTypeProperties {
  assetTypeToReturn: AssetTypeEnum;
  fallbackImg?: string;
  width?: number;
  height?: number;
  lang?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AssetUploadService {
  assetRefsSubject$ = new BehaviorSubject<AnyType>({});

  constructor(
    private assetsService: AssetsApiService,
    private clientNotificationHandlerService: ClientNotificationHandlerService,
    private fileUploadService: FileUploadService,
    private bannerHtmlService: AssetBannerHtmlService,
  ) {}

  async createAsset(fileObj: FileObject, formValues: CreateAssetDto) {
    if (formValues.name === '' || !formValues.name) {
      formValues.name = fileObj.name;
    }
    const assetTypeContents = await this.checkAssetType(fileObj);
    formValues.assetType = assetTypeContents.assetTypeToReturn as CreateAssetDto.AssetTypeEnum;
    formValues.mainFile = {
      name: fileObj.name,
      mimetype: fileObj.file.type,
      size: fileObj.file.size,
      ext: fileObj.file.name.split('.').pop() || '',
    };
    // only for banner having fallback image and metadata
    if (assetTypeContents.assetTypeToReturn === AssetTypeEnum.HtmlBanner) {
      formValues.mainFile = {
        ...formValues.mainFile,
        fallbackImg: assetTypeContents.fallbackImg,
        metadata: {
          bannerMetadata: {
            width: assetTypeContents.width,
            height: assetTypeContents.height,
          },
        },
      };
    }

    const res = await lastValueFrom(this.assetsService.assetsControllerCreate(formValues));
    this.clientNotificationHandlerService.successNotification($localize`Asset Created for` + ` ${res.name}`);
    return res;
  }

  async updateAspectRatio(fileObj: FileObject, formValues: AnyType) {
    if (formValues.name === '') {
      const nameArr = fileObj.name.split('.');
      nameArr.pop();
      formValues.name = nameArr.join('.');
    }

    const dto: UpdateAssetAspectRatioDto = {
      aspectRatio: formValues['aspect_ratio'],
      fileFormats: [
        {
          name: fileObj.name,
          assetMedium: formValues?.assetMedium,
          audioFile: formValues?.audioFile,
          mimetype: fileObj.file.type,
          size: fileObj.file.size,
          ext: fileObj.file.name.split('.').pop() || '',
        },
      ],
    };

    const assetId = formValues.assetId;
    const res = await lastValueFrom(this.assetsService.assetsControllerUpdateAspectRatio(assetId, dto));
    this.clientNotificationHandlerService.successNotification($localize`Asset aspect ratio updated`);
    return res;
  }

  async updateAsset(assetID: string, fileObj: FileObject, formValues: AnyType) {
    const assetTypeContents = await this.checkAssetType(fileObj);
    formValues.assetType = assetTypeContents.assetTypeToReturn;
    const res = await lastValueFrom(this.assetsService.assetsControllerUpdate(assetID, formValues));
    this.clientNotificationHandlerService.successNotification($localize`Asset updated successfully`);
    return res;
  }

  async updateManyAsset(updateAssetMany: UpdateAssetManyDto) {
    const res: { success: boolean; count: number; updatedAssets: Asset[] } = await lastValueFrom(
      this.assetsService.assetsControllerUpdateMany(updateAssetMany),
    );
    if (res.success) this.clientNotificationHandlerService.successNotification($localize`Buyout rights successfully updated`);
    return res.updatedAssets;
  }

  async checkAssetType(fileObj: FileObject): Promise<AssetTypeProperties> {
    const returnContents: AssetTypeProperties = { assetTypeToReturn: AssetTypeEnum.Other };
    try {
      returnContents.assetTypeToReturn = assetTypeByMimetype(fileObj.file.type).id;
      // If a zip file is smaller than 1 MB and its a zip, consider it as banner
      if (returnContents.assetTypeToReturn === AssetTypeEnum.ZIP && +(fileObj.file.size / 1024 / 1024).toFixed(2) < 1) {
        const bannerContents = await this.bannerHtmlService.checkZipForHtml(fileObj.file);
        if (bannerContents.html) {
          returnContents.assetTypeToReturn = AssetTypeEnum.HtmlBanner;
          returnContents.fallbackImg = bannerContents.fallbackImg;
          returnContents.width = bannerContents.width;
          returnContents.height = bannerContents.height;
          returnContents.lang = bannerContents.lang;
        }
      }
      return returnContents;
    } catch (err) {
      this.clientNotificationHandlerService.errorNotification(err);
      return returnContents;
    }
  }

  async checkForIncompleteUploads(fileObj: FileObject, files: FileObject[]): Promise<boolean | { key: string; file: File }> {
    const name = this.fileUploadService.normalizeFileName(fileObj.name);
    const res = await this.fileUploadService.checkIfFileExists(name);
    if (res.count) {
      const fileIndex = files.findIndex((fileObj) => fileObj.name === res.result[0].name);
      const file = files[fileIndex];
      files[fileIndex] = { ...file, key: res.result[0].key, uploaded: false, uploading: false };
      return { key: res.result[0].key, file: fileObj.file };
    }
    return false;
  }

  updateFileDataOnStart(startedFileData: AnyType, files: FileObject[]): void {
    const fileIndex = files.findIndex((fileObj) => fileObj.key === startedFileData.key);
    const file = files[fileIndex];
    files[fileIndex] = { ...file, uploaded: false, uploading: true };
  }

  async uploadFile(fileObj: { key: string; file: File }) {
    await this.fileUploadService.startMultiPartUpload([fileObj]);
  }

  async uploadVersionFile(assetID: string, fileObj: { key: string; file: File; name: string }) {
    const assetNewVersion: CreateAssetFileDto = {
      name: fileObj.name,
      size: fileObj.file.size,
      mimetype: fileObj.file.type,
      ext: fileObj.file.name.split('.').pop() || '',
    };
    // create the new asset version
    const res = await lastValueFrom(this.assetsService.assetsControllerCreateNewVersion(assetID, assetNewVersion));
    // upload the main file for the new version
    await this.fileUploadService.startMultiPartUpload([fileObj]);

    this.clientNotificationHandlerService.successNotification($localize`New asset version created`);

    return res;
  }
}
